import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Trayectorias de Aprendizaje",
  "lead": "While most children now spend many years in school, they often emerge without even the most basic reading and math skills. Learning trajectories show how much children learn as they progress through school. They can help us understand the process that leads to low learning outcomes, when and how to intervene during that process, and what the simulated impact of different policies might be on the urgent challenge of low learning."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    h3: "h3",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Explorer, Figure, HighlightByIndex, IndicatorReference, RelatedMaterial, ScrollyArea, ScrollySection, ScrollyStep, ScrollySticky, SimulationsTab, TrajectoriesTab} = _components;
  if (!Explorer) _missingMdxReference("Explorer", true);
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollyStep) _missingMdxReference("ScrollyStep", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  if (!SimulationsTab) _missingMdxReference("SimulationsTab", true);
  if (!TrajectoriesTab) _missingMdxReference("TrajectoriesTab", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_LEARNING"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Las trayectorias de aprendizaje indican cuántos años o grados debe completar un niño para adquirir competencias básicas. Dado que examinan el ritmo de aprendizaje en un sistema, ayudan a comprender cuántos niños tienen dificultades para adquirir dichas competencias, cuándo y de qué modo se puede actuar, y qué efectos tendrán las distintas políticas a la hora de atajar el reto urgente que plantean los niveles bajos de aprendizaje."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "qué-información-aportan-las-trayectorias-de-aprendizaje",
      children: "¿Qué información aportan las trayectorias de aprendizaje?"
    }), "\n", _jsx(_components.p, {
      children: "Lo ideal es que las trayectorias de aprendizaje se correspondan con una línea ascendente y muy inclinada. No obstante, con demasiada frecuencia se corresponden con líneas planas que indican un ritmo de aprendizaje lento. Un aprendizaje lento al principio puede tener consecuencias a largo plazo, pues, cuando un niño se queda atrás, el plan de estudios sigue progresando hacia contenidos más avanzados y el niño carece de las competencias necesarias para seguirlo."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Las trayectorias de numerosos países demuestran que el ritmo de aprendizaje es demasiado lento desde los primeros niveles escolares."
      }), "  A pesar de que las metas mundiales y muchos de los planes de estudios nacionales asumen que los niños aprenderán a leer en los grados 1 y 2, solo una minoría de los niños lo consigue."]
    }), "\n", _jsx(IndicatorReference, {
      id: "4.1.1",
      label: "4.1.1a",
      children: _jsxs(_components.p, {
        children: ["Dichas competencias son prerrequisitos básicos para el aprendizaje futuro, y se miden por la capacidad de un niño de leer en voz alta una historia sencilla de 70 palabras (por ejemplo,  “Manh está en la clase número dos.”) y responder a cinco preguntas sencillas al respecto (por ejemplo,  “¿En qué clase está Manh?”). Estas competencias básicas suelen estar por debajo del nivel mínimo de competencias establecido en ", _jsx(_components.a, {
          href: "https://sdgs.un.org/goals/goal4",
          children: "el indicador mundial 4.1.1a para los Objetivos de Desarrollo Sostenible (ODS)"
        }), ". Las trayectorias calculadas, por tanto, sobrestiman los avances hacia la consecución del ODS 4.1.1a, pero siguen siendo útiles para representar el ritmo de aprendizaje."]
      })
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "TRAJECTORIES_CURRENT"
        })
      }), _jsx(ScrollySection, {
        id: "HIGHLIGHT_VIETNAM",
        children: _jsxs(_components.p, {
          children: ["La trayectoria de ", _jsx(HighlightByIndex, {
            index: "2",
            children: "Viet Nam"
          }), " es un buen ejemplo de un país con un desempeño relativamente alto. Durante los primeros   grados escolares, la mayoría de los niños de Viet Nam adquieren competencias de lectura básicas."]
        })
      }), _jsx(ScrollySection, {
        id: "LMIC_GRADE_3",
        children: _jsxs(_components.p, {
          children: ["La trayectoria de aprendizaje en muchos de los países de", " ", "\n", _jsx(HighlightByIndex, {
            index: "4",
            children: "ingreso bajo y mediano bajo"
          }), " ", "\nes mucho más plana. Actualmente, solo una media de uno de cada cuatro niños del grado 3 ha adquirido competencias de lectura básicas."]
        })
      }), _jsx(ScrollySection, {
        id: "LMIC_GRADE_6",
        children: _jsxs(_components.p, {
          children: ["Los niveles bajos de aprendizaje persisten a medida que los niños avanzan en el colegio. De media, en los países de", " ", "\n", _jsx(HighlightByIndex, {
            index: "4",
            children: "ingreso bajo y mediano bajo"
          }), " ", "\ndel conjunto de datos solo algo más de la mitad del alumnado del grado 6 ha adquirido las competencias de lectura que habitualmente corresponden a los grados 2-3."]
        })
      }), _jsx(ScrollySection, {
        id: "DIFFERENCE_TRAJECTORIES",
        children: _jsx(_components.p, {
          children: "No obstante, la trayectoria media esconde diferencias entre países. En los países de ingreso bajo y mediano bajo del conjunto de datos, el porcentaje de niños que saben leer en el grado 3 puede estar entre un 3 y un 76 por ciento. Ello demuestra que los países que se comprometen a mejorar el aprendizaje básico pueden lograrlo, incluso aunque sus ingresos nacionales sean relativamente bajos."
        })
      }), _jsx(ScrollyStep, {
        id: "default"
      })]
    }), "\n", _jsx(_components.p, {
      children: "Los países que deseen atajar los bajos niveles de aprendizaje deben tener en cuenta principalmente las siguientes políticas:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "cuantificar y priorizar las competencias básicas desde los primeros grados;"
      }), "\n", _jsx(_components.li, {
        children: "adecuar la enseñanza al ritmo de aprendizaje real de los niños. Esto se puede llevar a cabo de diversas formas, como definiendo objetivos de aprendizaje claros que se centren en el aprendizaje de competencias básicas, ajustando el ritmo del plan de estudios, o brindando apoyo a los docentes para que comprendan el nivel de aprendizaje actual de los niños y adapten su enseñanza a este."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "simulaciones-de-políticas-acceso-y-aprendizaje",
      children: "Simulaciones de políticas: acceso y aprendizaje"
    }), "\n", _jsx(_components.p, {
      children: "Las trayectorias de aprendizaje pueden servir para comparar los potenciales efectos de las distintas políticas. De lo que se trata es de simular los efectos de las políticas que tienen por objetivo ampliar el acceso y mejorar el aprendizaje. Las políticas orientadas al acceso tienen por fin aumentar el número de años que los niños están escolarizados. Entre ellas se incluyen políticas como la gratuidad de la educación primaria y secundaria, la construcción de escuelas, el paso automático de un curso al siguiente y otras políticas que se centran principalmente en la matriculación o en el nivel de instrucción. Las políticas orientadas al aprendizaje tienen por objetivo aumentar la cantidad de conocimientos que los niños adquieren en cada grado escolar. Entre ellas se incluyen políticas como, por ejemplo, definir y cuantificar los avances hacia la consecución de unos objetivos de aprendizaje claros, ajustar la enseñanza al ritmo de aprendizaje de los niños, o respaldar el aprendizaje efectivo."
    }), "\n", _jsxs(_components.p, {
      children: ["Todos los niños deben tener acceso a la escuela, y conseguir que los niños vayan a la escuela es un primer paso imprescindible para el aprendizaje universal. Sin embargo, las simulaciones ponen de manifiesto que seguir ", _jsx(HighlightByIndex, {
        index: "2",
        children: "ampliando el acceso"
      }), ", por sí solo, apenas mejora el aprendizaje. De hecho, puede resultar más provechoso ", _jsx(HighlightByIndex, {
        index: "3",
        children: "aumentar los conocimientos adquiridos en cada grado"
      }), " para acercarse al ritmo de los países de ingreso bajo y mediano bajo que presentan un desempeño más alto."]
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "TRAJECTORIES_SIMULATION_LMIC"
        })
      }), _jsx(ScrollySection, {
        id: "COMPARISON",
        children: _jsx(_components.p, {
          children: "Las diferencias de desempeño entre los países con un desempeño más bajo y aquellos con un desempeño más alto se deben tanto a la mejora del acceso a la escuela como, en particular, al aumento de los conocimientos que los niños adquieren en cada grado."
        })
      }), _jsx(ScrollySection, {
        id: "FADED",
        children: _jsx(_components.p, {
          children: "Estas simulaciones muestran trayectorias por edad que incluyen tanto a los niños escolarizados como a los niños sin escolarizar. Dado que los niños sin escolarizar suelen presentar niveles de aprendizaje más bajos, estas trayectorias de aprendizaje son más planas que las trayectorias por grado."
        })
      }), _jsx(ScrollySection, {
        id: "CURRENT",
        children: _jsxs(_components.p, {
          children: ["De media, en cinco países de", " ", "\n", _jsx(HighlightByIndex, {
            index: "4",
            children: "ingreso bajo y mediano bajo"
          }), " ", "\nque presentan un desempeño bajo solo el 8 por ciento de los niños de 9 años de edad sabe leer con un nivel adecuado para su edad."]
        })
      }), _jsx(ScrollySection, {
        id: "SIMULATION_ACCESS",
        children: _jsxs(_components.p, {
          children: ["Las ", _jsx(HighlightByIndex, {
            index: "2",
            children: "simulaciones de acceso"
          }), " ", "\nse proponen estimar qué ocurriría si todos los niños estuvieran escolarizados. Para ello, se estima qué ocurriría si todos los niños que nunca han estado escolarizados, o que han abandonado la escuela, hubiesen alcanzado el mismo grado y nivel de aprendizaje que la media de niños escolarizados con su misma edad. En los cinco países con un desempeño bajo, la simulación revela que el número de niños de 9 años de edad que sabe leer aumentaría un punto porcentual."]
        })
      }), _jsx(ScrollySection, {
        id: "SIMULATION_LEARNING",
        children: _jsxs(_components.p, {
          children: ["Si los niños de 9 años de dichos países siguieran alcanzando el mismo grado escolar que alcanzan actualmente, solo que", " ", "\n", _jsx(HighlightByIndex, {
            index: "3",
            children: "adquiriendo tantos conocimientos por grado"
          }), " como los niños de los cinco países de ingreso bajo y mediano bajo que presentan un desempeño más alto, el 43 por ciento sabría leer, lo que supondría un aumento de 35 puntos porcentuales."]
        })
      }), _jsx(ScrollyStep, {
        id: "default"
      })]
    }), "\n", _jsx(_components.h2, {
      id: "simulaciones-de-políticas-igualdad",
      children: "Simulaciones de políticas: igualdad"
    }), "\n", _jsx(_components.p, {
      children: "Las trayectorias de aprendizaje son una herramienta que también permite comprender las diferencias de aprendizaje entre los distintos grupos de un mismo país. De lo que se trata es de comparar el aprendizaje de los ricos con el de los pobres y simular los efectos que tendrían las políticas de reducción de la desigualdad entre ellos."
    }), "\n", _jsxs(_components.p, {
      children: ["De media en todos los países de ingreso bajo y mediano bajo del conjunto de datos, los niños de familias ricas (familias que pertenecen al 20 por ciento más rico) van más a la escuela y aprenden más en cada grado que los niños de familias pobres (familias que pertenecen al 20 por ciento más pobre). La ", _jsx(HighlightByIndex, {
        index: "2",
        children: "simulación de la igualdad de acceso"
      }), " muestra qué ocurriría si los niños pobres presentaran los mismo niveles de escolarización que los ricos, y la ", _jsx(HighlightByIndex, {
        index: "3",
        children: "simulación de la igualdad de aprendizaje"
      }), " muestra qué ocurriría si los niños pobres adquirieran tantos conocimientos por grado como los niños ricos. En ambos casos descendería la desigualdad, sobre todo en la simulación de la igualdad de aprendizaje."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "No obstante, tampoco los niños de familias ricas reciben una educación muy buena"
      }), ". Casi la mitad de los niños de familias ricas no consiguen adquirir competencias de lectura básicas, lo que limita los posibles efectos de las políticas orientadas a la igualdad. Las simulaciones de igualdad entre niños y niñas, o entre niños de zonas urbanas y rurales, reportan mejoras aún menores."]
    }), "\n", _jsxs(_components.p, {
      children: ["Los países que deseen atajar los bajos niveles de aprendizaje deben entender que reducir las desigualdades entre los grupos de un mismo país es solo una parte de la solución. ", _jsx(_components.strong, {
        children: "Aplicar cambios sistémicos que mejoren el conocimiento de todos los niños, ya sean ricos o pobres, niños o niñas, de zonas urbanas o de zonas rurales, supone un reto igualmente acuciante para conseguir transformar un sistema con un rendimiento bajo en uno con un rendimiento alto."
      })]
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "TRAJECTORIES_SIMULATION_EQUALITY"
        })
      }), _jsx(ScrollySection, {
        id: "CURRENT",
        children: _jsxs(_components.p, {
          children: ["Los niños de familia ", _jsx(HighlightByIndex, {
            index: "4",
            children: "pobres"
          }), " presentan una trayectoria de aprendizaje más baja que los niños de familias ricas. En 23 países de ingreso bajo y mediano bajo, solo una media del 19 por ciento de los niños de familias pobres han adquirido competencias de lectura básicas a los 9 años de edad."]
        })
      }), _jsx(ScrollySection, {
        id: "SIMULATION_ACCESS",
        children: _jsxs(_components.p, {
          children: ["La ", _jsx(HighlightByIndex, {
            index: "2",
            children: "primera simulación"
          }), " muestra qué ocurriría si los niños de familias pobres presentaran los mismos niveles de escolarización que los niños de familias ricas (en lo que respecta al nivel de instrucción por edad). De media en 23 países, las competencias de lectura básicas a los 9 años de edad aumentarían solo cuatro puntos porcentuales."]
        })
      }), _jsx(ScrollySection, {
        id: "SIMULATION_LEARNING",
        children: _jsxs(_components.p, {
          children: ["La ", _jsx(HighlightByIndex, {
            index: "3",
            children: "segunda simulación"
          }), " muestra qué ocurriría si los niños de familias pobres siguieran presentando los niveles de escolarización actuales (es decir, si llegaran al mismo grado al que llegan ahora) pero adquirieran tantos conocimientos por grado como los niños de familias ricas. Esto haría que las competencias de lectura básicas a los 9 años de edad aumentaran, potencialmente, 17 puntos porcentuales."]
        })
      }), _jsx(ScrollySection, {
        id: "COMPARISON",
        children: _jsx(_components.p, {
          children: "Si los niños de familias pobres presentaran los mismos niveles de escolarización y, además, adquirieran tantos conocimientos como los niños ricos (es decir, si los pobres tuvieran la misma trayectoria que los ricos), las competencias de lectura básicas de los niños de familias pobres a los 9 años de edad podrían crecer hasta 29 puntos porcentuales."
        })
      }), _jsx(ScrollySection, {
        id: "COMPARISON_GROUP",
        children: _jsx(_components.p, {
          children: "No obstante, casi la mitad de los niños seguirían sin adquirir competencias de lectura básicas. En muchos países, ni siquiera los niños de familias ricas reciben una buena educación."
        })
      }), _jsx(ScrollyStep, {
        id: "default"
      })]
    }), "\n", _jsx(_components.h2, {
      id: "realice-sus-propias-trayectorias-de-aprendizaje-y-simulaciones-de-políticas",
      children: "Realice sus propias trayectorias de aprendizaje y simulaciones de políticas"
    }), "\n", _jsx(_components.p, {
      children: "A continuación encontrará una herramienta con la que podrá crear sus propias trayectorias de aprendizaje y realizar simulaciones de políticas. Puede consultar los datos de diferentes países sobre competencias básicas, tanto matemáticas como de lectura."
    }), "\n", _jsxs(_components.p, {
      children: ["Le recordamos que las competencias de lectura se miden por la capacidad de los niños de leer en voz alta una historia sencilla de 70 palabras y responder cinco preguntas sencillas sobre la historia. Las competencias matemáticas se miden mediante 21 preguntas sencillas en torno a cuatro ámbitos (la identificación de los números, la diferenciación de los números, la suma simple y las series numéricas). Se considera que un niño ha adquirido las competencias básicas matemáticas y de lectura si responde a todas las preguntas correctamente. Dichas habilidades suelen corresponder al nivel del grado 2 o 3 y están por debajo del nivel mínimo de competencias establecido en ", _jsx(_components.a, {
        href: "https://sdgs.un.org/goals/goal4",
        children: "el indicador mundial 4.1.1a para los ODS"
      }), "."]
    }), "\n", _jsxs(Explorer, {
      children: [_jsx(TrajectoriesTab, {
        children: _jsx(_components.p, {
          children: "Las trayectorias de aprendizaje varían enormemente según el contexto. Esta herramienta le permite generar, comparar y exportar las trayectorias que sean más relevantes para usted. Puede consultar y comparar las trayectorias de los diferentes países, o generar y comparar las trayectorias de los distintos grupos dentro de un país."
        })
      }), _jsx(SimulationsTab, {
        children: _jsx(_components.p, {
          children: "Estas herramientas permiten generar una simulación de políticas y analizar los resultados a fin de orientar las prioridades políticas. Se pueden realizar simulaciones de políticas nacionales, en las que se estiman los efectos de la escolarización universal y de conseguir el mismo nivel de aprendizaje por grado que otro país. Asimismo, se pueden realizar simulaciones de políticas para grupos de un mismo país, en las que se simulan los efectos de que dos grupos de población distintos presenten un nivel de instrucción y un aprendizaje similares."
        })
      })]
    }), "\n", _jsx(_components.h2, {
      id: "conclusión",
      children: "Conclusión"
    }), "\n", _jsx(_components.p, {
      children: "Las trayectorias de aprendizaje representan el ritmo al que los niños adquieren competencias básicas. Son una herramienta de relevancia que permiten concienciar a la opinión pública acerca del bajo nivel de los resultados del aprendizaje y ayudar a los encargados de la formulación de políticas a ponerse manos a la obra."
    }), "\n", _jsx(_components.h3, {
      id: "agradecimientos",
      children: "Agradecimientos"
    }), "\n", _jsxs(_components.p, {
      children: ["El contenido de la presente página web han sido elaborados por el ", _jsx(_components.a, {
        href: "http://riseprogramme.org/",
        children: "Programa RISE (siglas en inglés de Research on Improving Systems of Education, Investigación para la Mejora de los Sistemas Educativos)"
      }), ", un programa de investigación a gran escala sobre sistemas educativos que cuenta con financiación del ", _jsx(_components.a, {
        href: "https://www.gov.uk/government/organisations/foreign-commonwealth-development-office",
        children: "Ministerio de Relaciones Exteriores, del Commonwealth y de Desarrollo del Reino Unido"
      }), ", el ", _jsx(_components.a, {
        href: "http://dfat.gov.au",
        children: "Ministerio de Asuntos Exteriores y Comercio del Gobierno de Australia"
      }), " y la ", _jsx(_components.a, {
        href: "https://www.gatesfoundation.org",
        children: "Fundación Bill y Melinda Gates"
      }), ". Una alianza entre ", _jsx(_components.a, {
        href: "https://www.opml.co.uk",
        children: "Oxford Policy Management"
      }), " y la ", _jsx(_components.a, {
        href: "http://bsg.ox.ac.uk",
        children: "Escuela de Gobierno Blavatnik de la Universidad de Oxford"
      }), " se encarga de la gestión e implementación del programa."]
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://riseprogramme.org/tools/learning-trajectories",
            children: "Learning trajectories resources – RISE"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.sciencedirect.com/journal/international-journal-of-educational-development/special-issue/1035CNWP9N3",
            children: "Special issue on learning profiles (open source) – International Journal of Educational Development (IJED)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://mics.unicef.org/files?job=W1siZiIsIjIwMjAvMDUvMTIvMTgvMjUvNDUvNzAxLzIwXzA1XzA4X01JQ1NfR3VpZGVib29rX2NvbXByZXNzZWQucGRmIl1d&sha=6d386818d588d05c",
            children: "How to analyze MICS6 education data - UNICEF"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://riseprogramme.org/publications/descriptive-learning-trajectories-and-policy-simulations-using-mics6-data",
            children: "Simulations methodology – RISE"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
